import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { AlertTriangle, Clock, Trash2, X } from "lucide-react";
import { describeRetention } from "../../utils/deletionWindow";

/**
 * Super admin confirmation for deleting a shared project.
 *
 * Owners delete their own projects without a prompt (unchanged). A super admin
 * removes the project and every conversation in it for everyone, so this
 * dialog spells that out and offers the BrainBank cascade as an opt-in. The
 * delete waits in Recently deleted for `retentionHours` before it is permanent.
 */
interface DeleteProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  conversationCount: number;
  isDeleting: boolean;
  /** From /user/me (deletion_retention_hours); defaults to 24. */
  retentionHours?: number;
  onConfirm: (deleteBrainBanks: boolean) => void;
}

const DeleteProjectDialog: React.FC<DeleteProjectDialogProps> = ({
  isOpen,
  onClose,
  projectName,
  conversationCount,
  isDeleting,
  retentionHours,
  onConfirm,
}) => {
  const [deleteBrainBanks, setDeleteBrainBanks] = useState(false);

  // The checkbox is destructive, so it never carries over between projects.
  useEffect(() => {
    if (isOpen) setDeleteBrainBanks(false);
  }, [isOpen]);

  const conversationLabel =
    conversationCount === 1 ? "1 conversation" : `${conversationCount} conversations`;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (!isDeleting) onClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Delete shared project
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    disabled={isDeleting}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                </div>

                <p className="text-sm text-gray-700">
                  <span className="font-medium">{projectName}</span> and the{" "}
                  {conversationLabel} in it will be removed for everyone,
                  including their uploaded files.
                </p>

                <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5 text-xs text-amber-800">
                  <Clock size={14} className="mt-0.5 flex-shrink-0" />
                  <span>
                    You have {describeRetention(retentionHours)} to change your
                    mind. Until then you can restore it from Chat History ›
                    Recently deleted.
                  </span>
                </div>

                <label className="mt-4 flex items-start gap-2.5 rounded-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    checked={deleteBrainBanks}
                    disabled={isDeleting}
                    onChange={(e) => setDeleteBrainBanks(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">
                    Also delete the BrainBanks these conversations used, and all
                    of their files
                  </span>
                </label>

                {deleteBrainBanks && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2.5 text-xs text-red-700">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    <span>
                      A BrainBank is shared. Deleting it removes it for every
                      user and Virtual Assistant that uses it, not only this
                      project.
                    </span>
                  </div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => onConfirm(deleteBrainBanks)}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
                  >
                    {isDeleting ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    ) : (
                      <Trash2 size={14} className="mr-2" />
                    )}
                    {deleteBrainBanks ? "Delete project and BrainBanks" : "Delete project"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DeleteProjectDialog;
